import * as THREE from 'three';

// Enemy states
const STATE = {
  SPAWNING: 0,
  APPROACHING: 1,
  ATTACKING: 2,
  STRAFING: 3,
  DYING: 4,
};

// Enemy type configs
export const ENEMY_TYPES = {
  drone: {
    health: 20,
    speed: 3,
    damage: 5,
    collisionDamage: 8,
    points: 100,
    fireRate: 0.8,    // shots per second
    projectileSpeed: 15,
    color: 0x44ff44,
    emissive: 0x22aa22,
    size: 0.75,
    attackRange: 20,
    preferredRange: 12,
  },
  soldier: {
    health: 40,
    speed: 4,
    damage: 8,
    collisionDamage: 12,
    points: 250,
    fireRate: 1.2,
    projectileSpeed: 18,
    color: 0x4488ff,
    emissive: 0x2244aa,
    size: 0.95,
    attackRange: 22,
    preferredRange: 10,
    burstCount: 3,
    burstDelay: 0.15,
  },
};

const POOL_SIZE = 40;
const BASE_ENEMY_SIZE = 0.5; // Original geometry scale used for eye proportions
const BASE_EYE_Z_OFFSET = 0.4;

export class EnemyManager {
  constructor(scene) {
    this.scene = scene;
    this.enemies = [];
    this.active = [];

    // Pre-create enemy pool
    for (let i = 0; i < POOL_SIZE; i++) {
      const enemy = new EnemyInstance(scene);
      this.enemies.push(enemy);
    }
  }

  spawn(type, position) {
    const config = ENEMY_TYPES[type];
    if (!config) return null;

    const enemy = this.enemies.find(e => !e.active);
    if (!enemy) return null;

    enemy.activate(type, config, position);
    this.active.push(enemy);
    return enemy;
  }

  update(dt, playerPos, projectilePool, audio) {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const enemy = this.active[i];
      enemy.update(dt, playerPos, projectilePool, audio);

      if (!enemy.active) {
        this.active.splice(i, 1);
      }
    }
  }

  getActive() {
    return this.active;
  }

  get count() {
    return this.active.length;
  }

  reset() {
    for (const enemy of this.active) {
      enemy.deactivate();
    }
    this.active.length = 0;
  }
}

class EnemyInstance {
  constructor(scene) {
    this.scene = scene;
    this.active = false;
    this.type = 'drone';
    this.config = null;
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.health = 0;
    this.maxHealth = 0;
    this.state = STATE.SPAWNING;
    this.stateTimer = 0;
    this.fireTimer = 0;
    this.burstRemaining = 0;
    this.burstTimer = 0;
    this.strafeDir = 1;
    this.strafeTimer = 0;
    this.flashTimer = 0;
    this.deathTimer = 0;
    this.spawnTimer = 0;
    this.collisionCooldown = 0;

    // Main mesh group
    this.group = new THREE.Group();
    this.group.visible = false;
    scene.add(this.group);

    // Body mesh - pre-allocate both geometry types for reuse
    this.droneGeo = new THREE.OctahedronGeometry(0.5, 0);
    this.soldierGeo = new THREE.DodecahedronGeometry(0.5, 0);
    this.bodyMat = new THREE.MeshStandardMaterial({
      color: 0x44ff44,
      emissive: 0x22aa22,
      emissiveIntensity: 0.6,
      metalness: 0.5,
      roughness: 0.4,
    });
    this.body = new THREE.Mesh(this.droneGeo, this.bodyMat);
    this.group.add(this.body);

    // Health bar
    const hbGeo = new THREE.PlaneGeometry(0.8, 0.06);
    const hbBgMat = new THREE.MeshBasicMaterial({ color: 0x333333, side: THREE.DoubleSide });
    this.healthBarBg = new THREE.Mesh(hbGeo, hbBgMat);
    this.healthBarBg.position.y = 0.8;
    this.group.add(this.healthBarBg);

    const hbFillGeo = new THREE.PlaneGeometry(0.8, 0.06);
    const hbFillMat = new THREE.MeshBasicMaterial({ color: 0xff0000, side: THREE.DoubleSide });
    this.healthBarFill = new THREE.Mesh(hbFillGeo, hbFillMat);
    this.healthBarFill.position.y = 0.8;
    this.healthBarFill.position.z = 0.001;
    this.group.add(this.healthBarFill);

    // "Eye" - glowing center
    const eyeGeo = new THREE.SphereGeometry(0.1, 8, 8);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    this.eye = new THREE.Mesh(eyeGeo, eyeMat);
    this.eye.position.z = 0.4;
    this.group.add(this.eye);

    this._origColor = new THREE.Color();
    this._origEmissive = new THREE.Color();

    // Pre-allocated scratch vectors to avoid per-frame allocations
    this._toPlayer = new THREE.Vector3();
    this._strafeVec = new THREE.Vector3();
  }

  activate(type, config, position) {
    this.active = true;
    this.type = type;
    this.config = config;
    this.position.copy(position);
    this.position.y = 1.2;
    this.velocity.set(0, 0, 0);
    this.health = config.health;
    this.maxHealth = config.health;
    this.state = STATE.SPAWNING;
    this.spawnTimer = 0.5;
    this.fireTimer = 1 + Math.random(); // initial delay
    this.burstRemaining = 0;
    this.burstTimer = 0;
    this.strafeDir = Math.random() > 0.5 ? 1 : -1;
    this.strafeTimer = 2 + Math.random() * 2;
    this.flashTimer = 0;
    this.deathTimer = 0;
    this.collisionCooldown = 0;

    // Configure appearance based on type
    this.bodyMat.color.set(config.color);
    this.bodyMat.emissive.set(config.emissive);
    this._origColor.set(config.color);
    this._origEmissive.set(config.emissive);

    const s = config.size;

    // Different geometry per type - reuse pre-allocated geometries
    this.body.geometry = (type === 'soldier') ? this.soldierGeo : this.droneGeo;
    this.body.scale.setScalar(s);

    this.group.position.copy(this.position);
    this.group.visible = true;
    this.group.scale.setScalar(0.01);

    const sizeScale = s / BASE_ENEMY_SIZE;
    this.eye.scale.setScalar(sizeScale);
    this.eye.position.z = BASE_EYE_Z_OFFSET * sizeScale;
  }

  update(dt, playerPos, projectilePool, audio) {
    if (!this.active) return;

    switch (this.state) {
      case STATE.SPAWNING:
        this._updateSpawning(dt);
        break;
      case STATE.APPROACHING:
        this._updateApproaching(dt, playerPos);
        break;
      case STATE.ATTACKING:
        this._updateAttacking(dt, playerPos, projectilePool, audio);
        break;
      case STATE.STRAFING:
        this._updateStrafing(dt, playerPos, projectilePool, audio);
        break;
      case STATE.DYING:
        this._updateDying(dt);
        break;
    }

    // Collision cooldown
    if (this.collisionCooldown > 0) {
      this.collisionCooldown -= dt;
    }

    // Damage flash
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      if (this.flashTimer > 0) {
        this.bodyMat.emissive.set(0xffffff);
        this.bodyMat.emissiveIntensity = 2;
      } else {
        this.bodyMat.emissive.copy(this._origEmissive);
        this.bodyMat.emissiveIntensity = 0.6;
      }
    }

    // Floating bob
    if (this.state !== STATE.DYING) {
      this.position.y = 1.2 + Math.sin(Date.now() * 0.003 + this.position.x * 10) * 0.15;
    }

    // Update group position
    this.group.position.copy(this.position);

    // Face player (health bars)
    if (playerPos) {
      this.healthBarBg.lookAt(playerPos);
      this.healthBarFill.lookAt(playerPos);
    }

    // Update health bar
    const hpRatio = this.health / this.maxHealth;
    this.healthBarFill.scale.x = Math.max(0.001, hpRatio);
    this.healthBarFill.position.x = (hpRatio - 1) * 0.4;

    // Body rotation
    if (this.state !== STATE.DYING) {
      this.body.rotation.y += dt * 1.5;
      this.body.rotation.x += dt * 0.5;
    }
  }

  _updateSpawning(dt) {
    this.spawnTimer -= dt;
    const t = 1 - Math.max(0, this.spawnTimer / 0.5);
    this.group.scale.setScalar(t);
    if (this.spawnTimer <= 0) {
      this.group.scale.setScalar(1);
      this.state = STATE.APPROACHING;
    }
  }

  _updateApproaching(dt, playerPos) {
    if (!playerPos) return;
    const toPlayer = this._toPlayer.subVectors(playerPos, this.position);
    toPlayer.y = 0;
    const dist = toPlayer.length();

    if (dist > this.config.preferredRange) {
      toPlayer.normalize();
      this.position.addScaledVector(toPlayer, this.config.speed * dt);
    } else {
      // Switch to attack/strafe
      this.state = this.type === 'soldier' ? STATE.STRAFING : STATE.ATTACKING;
    }
  }

  _updateAttacking(dt, playerPos, projectilePool, audio) {
    if (!playerPos) return;

    const toPlayer = this._toPlayer.subVectors(playerPos, this.position);
    toPlayer.y = 0;
    const dist = toPlayer.length();

    // Maintain distance
    if (dist < this.config.preferredRange * 0.7) {
      toPlayer.normalize();
      this.position.addScaledVector(toPlayer, -this.config.speed * 0.5 * dt);
    }

    // Fire
    this._handleFiring(dt, playerPos, projectilePool, audio);
  }

  _updateStrafing(dt, playerPos, projectilePool, audio) {
    if (!playerPos) return;

    const toPlayer = this._toPlayer.subVectors(playerPos, this.position);
    toPlayer.y = 0;
    const dist = toPlayer.length();

    // Strafe perpendicular to player
    const strafeVec = this._strafeVec.set(-toPlayer.z, 0, toPlayer.x).normalize();
    this.position.addScaledVector(strafeVec, this.strafeDir * this.config.speed * 0.7 * dt);

    // Maintain distance
    if (dist < this.config.preferredRange * 0.6) {
      toPlayer.normalize();
      this.position.addScaledVector(toPlayer, -this.config.speed * 0.3 * dt);
    } else if (dist > this.config.preferredRange * 1.3) {
      toPlayer.normalize();
      this.position.addScaledVector(toPlayer, this.config.speed * 0.3 * dt);
    }

    // Change strafe direction
    this.strafeTimer -= dt;
    if (this.strafeTimer <= 0) {
      this.strafeDir *= -1;
      this.strafeTimer = 1.5 + Math.random() * 2;
    }

    // Keep in arena
    const posDist = Math.sqrt(this.position.x ** 2 + this.position.z ** 2);
    if (posDist > 18) {
      const scale = 18 / posDist;
      this.position.x *= scale;
      this.position.z *= scale;
    }

    this._handleFiring(dt, playerPos, projectilePool, audio);
  }

  _handleFiring(dt, playerPos, projectilePool, audio) {
    // Burst fire for soldiers
    if (this.burstRemaining > 0) {
      this.burstTimer -= dt;
      if (this.burstTimer <= 0) {
        this._fireProjectile(playerPos, projectilePool, audio);
        this.burstRemaining--;
        this.burstTimer = this.config.burstDelay || 0.15;
      }
      return;
    }

    this.fireTimer -= dt;
    if (this.fireTimer <= 0) {
      if (this.config.burstCount) {
        this.burstRemaining = this.config.burstCount;
        this.burstTimer = 0;
      } else {
        this._fireProjectile(playerPos, projectilePool, audio);
      }
      this.fireTimer = 1 / this.config.fireRate + Math.random() * 0.3;
    }
  }

  _fireProjectile(playerPos, projectilePool, audio) {
    const dir = this._toPlayer.subVectors(playerPos, this.position).normalize();
    // Add some inaccuracy
    dir.x += (Math.random() - 0.5) * 0.15;
    dir.y += (Math.random() - 0.5) * 0.1;
    dir.z += (Math.random() - 0.5) * 0.15;
    dir.normalize();

    projectilePool.spawn(
      this.position.clone(),
      dir,
      this.config.projectileSpeed,
      this.config.damage,
      false,
    );

    if (audio) audio.playEnemyShoot();
  }

  _updateDying(dt) {
    this.deathTimer += dt;
    const t = this.deathTimer / 0.4;

    this.group.scale.setScalar(Math.max(0.01, 1 - t));
    this.body.rotation.x += dt * 10;
    this.body.rotation.y += dt * 10;
    this.bodyMat.emissive.set(0xffffff);
    this.bodyMat.emissiveIntensity = 2 * (1 - t);

    if (t >= 1) {
      this.deactivate();
    }
  }

  takeDamage(amount) {
    if (this.state === STATE.DYING) return false;

    this.health -= amount;
    this.flashTimer = 0.1;

    if (this.health <= 0) {
      this.health = 0;
      this.state = STATE.DYING;
      this.deathTimer = 0;
      return true; // killed
    }
    return false;
  }

  deactivate() {
    this.active = false;
    this.group.visible = false;
    this.group.scale.setScalar(1);
    this.bodyMat.emissive.copy(this._origEmissive);
    this.bodyMat.emissiveIntensity = 0.6;
  }

  // Bounding sphere radius for collision
  get radius() {
    return (this.config?.size || 0.5) * 0.8;
  }
}
